import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import {
    Anchor,
    Button,
    Flex,
    FormHelp,
    Heading,
    Paragraph,
    Spacer,
} from '@zillow/constellation';

import ClaimListingCard from 'app/shared/components/ClaimListingCard';
import numberUtils from 'app/shared/utils/numberUtils';
import stringHelper from 'app/shared/utils/stringHelper';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';

import { CenteredSpacer } from '../../StyledComponents';
import {
    StyledHeading, StyledCenteredParagraph, StyledBar,
} from './StyledComponents';

const { pluralize } = stringHelper;
const { formatPhoneNumberInput } = numberUtils;

const propTypes = {
    contactMethod: PropTypes.oneOf(['email', 'phone']).isRequired,
    contactValue: PropTypes.string.isRequired,
    listings: PropTypes.array.isRequired,
    listingCount: PropTypes.number.isRequired,
    onAddContactMethod: PropTypes.func.isRequired,
    onChooseAnotherContactMethod: PropTypes.func.isRequired,
    onHelpClick: PropTypes.func.isRequired,
    errorMessage: PropTypes.string.isRequired,
};

const SearchResults = ({
    contactMethod,
    contactValue,
    listings,
    listingCount,
    onAddContactMethod,
    onChooseAnotherContactMethod,
    onHelpClick,
    errorMessage,
}) => {
    const dispatch = useDispatch();

    const handleSearchAnotherContact = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK,
            {
                label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.LISTING_RESULTS_MODAL,
                action: listings.length > 0 ? 'SearchAnotherContact' : 'NoResultsSearchAnotherContact',
            },
        ));
        onChooseAnotherContactMethod();
    };

    const handleAddContactMethod = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK,
            {
                label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.LISTING_RESULTS_MODAL,
                action: 'NoResultsVerifyAnyway',
            },
        ));
        onAddContactMethod();
    };

    const renderListings = () => (
        <>
            <CenteredSpacer marginBottom="xs">
                <Heading level={6}>
                    {`We found ${pluralize(listingCount, 'listing', 'listings')}`}
                </Heading>
                <Paragraph marginBottom="xs">
                    {contactMethod === 'phone' ?
                        formatPhoneNumberInput(contactValue) : contactValue}
                </Paragraph>
                <Paragraph marginBottom="xs">
                    <b>Not your listings? </b>
                    Try another
                    {' '}
                    <Anchor onClick={handleSearchAnotherContact}><b>contact</b></Anchor>
                    {false && (
                        <span>
                            {' '}
                            or see
                            {' '}
                            <Anchor onClick={onHelpClick}><b>help</b></Anchor>
                        </span>
                    )}
                </Paragraph>
            </CenteredSpacer>
            <div>
                {listings.map((listing, i) => (
                    <ClaimListingCard
                        key={`${listing.listingAlias}-${i}`}
                        listingData={listing}
                        highlightEmail={contactMethod === 'email'}
                        highlightPhone={contactMethod === 'phone'}
                    />
                ))}
            </div>
        </>
    );

    const renderNoListingsFound = () => (
        <>
            <CenteredSpacer marginTop="xl" marginBottom="sm">
                <StyledHeading level={4}>
                    {`We didn't find any listings for ${contactMethod === 'phone' ?
                        formatPhoneNumberInput(contactValue) : contactValue}`}
                </StyledHeading>
            </CenteredSpacer>
            <Flex display="flex" flexDirection="column" alignItems="center">
                <Button
                    buttonType="primary"
                    size="lg"
                    onClick={handleSearchAnotherContact}
                >
                    Try another contact method
                </Button>
                <StyledBar />
                <StyledCenteredParagraph marginBottom="xs">
                    {`If you’ll use ${contactMethod === 'phone' ? formatPhoneNumberInput(contactValue) :
                        contactValue} for future listings, add it to your contact methods now to save time later.`}
                </StyledCenteredParagraph>
                <Button buttonType="secondary" size="lg" onClick={handleAddContactMethod} disabled={!!errorMessage}>
                    {contactMethod === 'phone' ? 'Add phone number' : 'Add email'}
                </Button>
                {!!errorMessage && (
                    <Spacer marginY="xs">
                        <FormHelp error={true}>{errorMessage}</FormHelp>
                    </Spacer>
                )}
            </Flex>
        </>
    );

    return listingCount > 0 ? renderListings() : renderNoListingsFound();
};

SearchResults.propTypes = propTypes;

export default SearchResults;
